import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Stack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Button,
  Link,
  Divider,
  Checkbox,
  useToast,
} from "@chakra-ui/react";
import { Eye, EyeOff } from "../../../shared/icons";
import { trackEvent } from "../../../shared/lib/analytics";
import { signupSchema } from "../schemas/authSchemas";
import { useSignupMutation } from "../hooks/useSignupMutation";
import GoogleLoginButton from "../components/GoogleLoginButton";
import TermsConsentLabel from "../../terms/components/TermsConsentLabel";

export default function SignUpPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const signupMutation = useSignupMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(signupSchema),
    defaultValues: { nomeCompleto: "", email: "", password: "", confirmarSenha: "", acceptTerms: false },
  });

  const onSubmit = (data) => {
    signupMutation.mutate(
      { email: data.email, password: data.password, nomeCompleto: data.nomeCompleto, acceptTerms: data.acceptTerms },
      {
        onSuccess: () => {
          trackEvent("signup_completed");
          toast({
            title: "Conta criada",
            description: "Enviamos um link de verificação para o seu e-mail.",
            status: "success",
            duration: 6000,
            isClosable: true,
          });
          navigate(`/auth/login?email=${encodeURIComponent(data.email)}`);
        },
        onError: (error) => {
          toast({
            title: "Não foi possível criar sua conta",
            description: error.response?.data?.mensagem || "Tente novamente em instantes.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        },
      }
    );
  };

  return (
    <Stack spacing={6}>
      <Stack spacing={1} textAlign="center">
        <Heading size="md">Criar sua conta</Heading>
        <Text color="app-muted-fg" fontSize="sm">
          Comece a controlar seus recebíveis em minutos
        </Text>
      </Stack>

      <GoogleLoginButton />

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={4}>
          <FormControl isInvalid={Boolean(errors.nomeCompleto)}>
            <FormLabel htmlFor="nomeCompleto">Nome completo</FormLabel>
            <Input id="nomeCompleto" autoComplete="name" autoFocus {...register("nomeCompleto")} />
            <FormErrorMessage>{errors.nomeCompleto?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={Boolean(errors.email)}>
            <FormLabel htmlFor="email">E-mail</FormLabel>
            <Input id="email" type="email" autoComplete="email" {...register("email")} />
            <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={Boolean(errors.password)}>
            <FormLabel htmlFor="password">Senha</FormLabel>
            <InputGroup>
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                {...register("password")}
              />
              <InputRightElement>
                <IconButton
                  variant="ghost"
                  size="sm"
                  icon={showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                />
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={Boolean(errors.confirmarSenha)}>
            <FormLabel htmlFor="confirmarSenha">Confirmar senha</FormLabel>
            <Input id="confirmarSenha" type={showPassword ? "text" : "password"} autoComplete="new-password" {...register("confirmarSenha")} />
            <FormErrorMessage>{errors.confirmarSenha?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={Boolean(errors.acceptTerms)}>
            <Checkbox id="acceptTerms" alignItems="flex-start" {...register("acceptTerms")}>
              <TermsConsentLabel />
            </Checkbox>
            <FormErrorMessage>{errors.acceptTerms?.message}</FormErrorMessage>
          </FormControl>

          <Button type="submit" size="lg" isLoading={signupMutation.isPending}>
            Criar conta
          </Button>
        </Stack>
      </form>

      <Stack spacing={4}>
        <Divider />
        <Text textAlign="center" fontSize="sm">
          Já tem conta?{" "}
          <Link as={RouterLink} to="/auth/login" color="accent-text" fontWeight="semibold">
            Entrar
          </Link>
        </Text>
      </Stack>
    </Stack>
  );
}
